/**
 * TemplateEditor component - Tabbed template creation/editing form
 *
 * Phase 25 rewrite: Restructured with 4 tabs (Gates, Diagnosis, Performance KPIs,
 * Verdict Rules). If no template exists, shows StarterSelector first.
 * Forking a starter populates all sections.
 *
 * "Guided process" feel — tabs are walkable steps, not a wall of settings.
 */

import * as TabsPrimitive from '@radix-ui/react-tabs';
import {
  ChevronRight,
  Plus,
  Save,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ALL_STARTER_TEMPLATES } from '@/constants/starter-templates';
import { cn } from '@/lib/utils';
import type {
  CreateTemplateRequest,
  DiagnosisDimension,
  Gate,
  KpiConfig,
  KpiOption,
  PercentileSettings,
  Template,
  UpdateTemplateRequest,
  VerdictRule,
} from '@/types/template';

import { DiagnosisSection } from './DiagnosisSection';
import { formatInstructions, GateEditor } from './GateEditor';
import { KpiSection } from './KpiSection';
import { StarterSelector } from './StarterSelector';
import { VerdictRuleBuilder } from './VerdictRuleBuilder';

// ============================================================================
// TYPES
// ============================================================================

export interface TemplateEditorProps {
  template?: Template;
  onSave: (data: CreateTemplateRequest | UpdateTemplateRequest) => void;
  onCancel: () => void;
  isSaving: boolean;
  customKpiOptions?: KpiOption[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_GATES = 20;

// ============================================================================
// HELPERS
// ============================================================================

function generateTempGateId(): string {
  const hex = Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `gate_${hex}`;
}

function createEmptyGate(): Gate {
  return {
    gateId: generateTempGateId(),
    name: '',
    instructions: formatInstructions('', '', ''),
    role: 'standard',
    isSystem: false,
  };
}

// ============================================================================
// TAB STYLING
// ============================================================================

function TabsList({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <TabsPrimitive.List
      className={cn(
        'flex gap-1 border-b border-border-subtle px-1 -mb-px',
        className,
      )}
    >
      {children}
    </TabsPrimitive.List>
  );
}

function TabsTrigger({
  children,
  value,
  className,
}: {
  children: React.ReactNode;
  value: string;
  className?: string;
}) {
  return (
    <TabsPrimitive.Trigger
      value={value}
      className={cn(
        'px-4 py-2.5 text-sm font-medium text-secondary transition-all',
        'border-b-2 border-transparent',
        'hover:text-primary hover:border-brand/30',
        'data-[state=active]:text-brand data-[state=active]:border-brand',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:rounded-t-md',
        className,
      )}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({
  children,
  value,
  className,
}: {
  children: React.ReactNode;
  value: string;
  className?: string;
}) {
  return (
    <TabsPrimitive.Content
      value={value}
      className={cn('pt-6 focus-visible:outline-none', className)}
    >
      {children}
    </TabsPrimitive.Content>
  );
}

// ============================================================================
// GATES TAB CONTENT (wraps existing GateEditor)
// ============================================================================

interface GatesTabProps {
  gates: Gate[];
  gateErrors: Record<string, string>;
  isSaving: boolean;
  onAddGate: () => void;
  onUpdateGate: (index: number, gate: Gate) => void;
  onRemoveGate: (index: number) => void;
  onMoveGateUp: (index: number) => void;
  onMoveGateDown: (index: number) => void;
}

function GatesTab({
  gates,
  gateErrors,
  isSaving,
  onAddGate,
  onUpdateGate,
  onRemoveGate,
  onMoveGateUp,
  onMoveGateDown,
}: GatesTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-primary">
          Quality Gates ({gates.length}/{MAX_GATES})
        </h3>
        <Button
          variant="secondary"
          size="sm"
          onClick={onAddGate}
          disabled={gates.length >= MAX_GATES || isSaving}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add Gate
        </Button>
      </div>

      {gates.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border-subtle rounded-lg">
          <p className="text-sm text-muted mb-4">
            No quality gates defined yet. Add gates to create evaluation criteria.
          </p>
          <Button
            variant="secondary"
            onClick={onAddGate}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Your First Gate
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {gates.map((gate, idx) => (
            <div key={gate.gateId}>
              <GateEditor
                gate={gate}
                index={idx}
                totalGates={gates.length}
                onUpdate={(updated) => onUpdateGate(idx, updated)}
                onRemove={() => onRemoveGate(idx)}
                onMoveUp={() => onMoveGateUp(idx)}
                onMoveDown={() => onMoveGateDown(idx)}
              />
              {gateErrors[gate.gateId] && (
                <p className="mt-1 text-xs text-danger">{gateErrors[gate.gateId]}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TemplateEditor({
  template,
  onSave,
  onCancel,
  isSaving,
  customKpiOptions = [],
}: TemplateEditorProps) {
  const isEditMode = !!template;

  // --- Core fields ---
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [gates, setGates] = useState<Gate[]>(template?.gates || []);
  const [nameError, setNameError] = useState('');
  const [gateErrors, setGateErrors] = useState<Record<string, string>>({});

  // --- Phase 25 fields (auto-populate from starter defaults when empty) ---
  const initialStarter = template
    ? ALL_STARTER_TEMPLATES.find((t) => t.key === (template.starterKey || 'standard'))
    : undefined;

  const [diagnosisDimensions, setDiagnosisDimensions] = useState<DiagnosisDimension[]>(
    () => {
      if (template?.diagnosisDimensions && template.diagnosisDimensions.length > 0) {
        return template.diagnosisDimensions;
      }
      if (initialStarter) {
        return initialStarter.dimensions.map((d) => ({ ...d, outputConfig: { ...d.outputConfig } }));
      }
      return [];
    },
  );
  const [kpiConfig, setKpiConfig] = useState<KpiConfig | null>(
    () => {
      if (template?.kpiConfig !== undefined && template?.kpiConfig !== null) {
        return template.kpiConfig;
      }
      if (initialStarter?.kpiConfig) {
        return { ...initialStarter.kpiConfig };
      }
      return null;
    },
  );
  const [verdictRulesV2, setVerdictRulesV2] = useState<VerdictRule[]>(
    () => {
      if (template?.verdictRulesV2 && template.verdictRulesV2.length > 0) {
        return template.verdictRulesV2;
      }
      if (initialStarter) {
        return initialStarter.verdictRules.map((r) => ({
          ...r,
          conditionGroups: r.conditionGroups.map((g) => ({
            ...g,
            conditions: g.conditions.map((c) => ({ ...c, source: { ...c.source } })),
          })),
        }));
      }
      return [];
    },
  );

  // --- Phase 26: Percentile settings ---
  const [percentileSettings, setPercentileSettings] = useState<PercentileSettings | undefined>(
    () => template?.percentileSettings ?? undefined,
  );

  // --- Phase 29: Template-level rolling window (propagated to all rules on save) ---
  const [rollingWindowDays, setRollingWindowDays] = useState<number>(
    () => {
      // Initialize from first rule's value (all rules share the same window)
      const existing = template?.verdictRulesV2?.[0]?.rollingWindowDays;
      return existing ?? 7;
    },
  );

  // --- Starter selection state (for new templates) ---
  const [selectedStarterKey, setSelectedStarterKey] = useState<string | null>(
    template?.starterKey || null
  );
  const [hasForked, setHasForked] = useState(isEditMode);

  // Reinitialize when template prop changes.
  // Auto-populate Phase 25 fields from starter defaults when missing —
  // existing templates created before Phase 25 won't have these fields.
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setGates(template.gates);
      setHasForked(true);

      // Determine starter to use for defaults
      const starterKey = template.starterKey || 'standard';
      const starter = ALL_STARTER_TEMPLATES.find((t) => t.key === starterKey);

      // Auto-populate diagnosisDimensions from starter if empty/missing
      if (template.diagnosisDimensions && template.diagnosisDimensions.length > 0) {
        setDiagnosisDimensions(template.diagnosisDimensions);
      } else if (starter) {
        setDiagnosisDimensions(
          starter.dimensions.map((d) => ({ ...d, outputConfig: { ...d.outputConfig } })),
        );
      } else {
        setDiagnosisDimensions([]);
      }

      // Auto-populate kpiConfig from starter if missing
      if (template.kpiConfig !== undefined && template.kpiConfig !== null) {
        setKpiConfig(template.kpiConfig);
      } else if (starter?.kpiConfig) {
        setKpiConfig({ ...starter.kpiConfig });
      } else {
        setKpiConfig(null);
      }

      // Auto-populate verdictRulesV2 from starter if empty/missing
      if (template.verdictRulesV2 && template.verdictRulesV2.length > 0) {
        setVerdictRulesV2(template.verdictRulesV2);
      } else if (starter) {
        setVerdictRulesV2(
          starter.verdictRules.map((r) => ({
            ...r,
            conditionGroups: r.conditionGroups.map((g) => ({
              ...g,
              conditions: g.conditions.map((c) => ({ ...c, source: { ...c.source } })),
            })),
          })),
        );
      } else {
        setVerdictRulesV2([]);
      }

      // Phase 26: Reinitialize percentile settings
      setPercentileSettings(template.percentileSettings ?? undefined);
    }
  }, [template]);

  // --- Derived state ---
  const enabledDimCount = useMemo(
    () => diagnosisDimensions.filter((d) => d.enabled).length,
    [diagnosisDimensions],
  );

  // ============================================================================
  // STARTER FORK
  // ============================================================================

  const handleForkStarter = useCallback(
    (starterKey: string) => {
      const starter = ALL_STARTER_TEMPLATES.find((t) => t.key === starterKey);
      if (!starter) return;

      setSelectedStarterKey(starterKey);
      setName(starter.name);
      setDescription(starter.description);

      // Deep copy gates
      setGates(starter.gates.map((g) => ({ ...g })));

      // Deep copy dimensions
      setDiagnosisDimensions(starter.dimensions.map((d) => ({ ...d, outputConfig: { ...d.outputConfig } })));

      // Copy KPI config
      setKpiConfig(starter.kpiConfig ? { ...starter.kpiConfig } : null);

      // Deep copy verdict rules
      setVerdictRulesV2(
        starter.verdictRules.map((r) => ({
          ...r,
          conditionGroups: r.conditionGroups.map((g) => ({
            ...g,
            conditions: g.conditions.map((c) => ({ ...c, source: { ...c.source } })),
          })),
        })),
      );

      setHasForked(true);
    },
    [],
  );

  // ============================================================================
  // GATE MANAGEMENT
  // ============================================================================

  const handleAddGate = useCallback(() => {
    if (gates.length >= MAX_GATES) return;
    setGates((prev) => [...prev, createEmptyGate()]);
  }, [gates.length]);

  const handleUpdateGate = useCallback((index: number, updatedGate: Gate) => {
    setGates((prev) => {
      const next = [...prev];
      next[index] = updatedGate;
      return next;
    });
    setGateErrors((prev) => {
      if (prev[updatedGate.gateId]) {
        const next = { ...prev };
        delete next[updatedGate.gateId];
        return next;
      }
      return prev;
    });
  }, []);

  const handleRemoveGate = useCallback((index: number) => {
    setGates((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMoveGateUp = useCallback((index: number) => {
    if (index === 0) return;
    setGates((prev) => {
      if (prev[index - 1]?.isSystem && !prev[index]?.isSystem) return prev;
      const next = [...prev];
      const temp = next[index] as Gate;
      next[index] = next[index - 1] as Gate;
      next[index - 1] = temp;
      return next;
    });
  }, []);

  const handleMoveGateDown = useCallback((index: number) => {
    setGates((prev) => {
      if (index >= prev.length - 1) return prev;
      if (prev[index]?.isSystem && !prev[index + 1]?.isSystem) return prev;
      const next = [...prev];
      const temp = next[index] as Gate;
      next[index] = next[index + 1] as Gate;
      next[index + 1] = temp;
      return next;
    });
  }, []);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validate = useCallback((): boolean => {
    let isValid = true;

    if (!name.trim()) {
      setNameError('Template name is required');
      isValid = false;
    } else if (name.trim().length > 100) {
      setNameError('Template name must be 100 characters or less');
      isValid = false;
    } else {
      setNameError('');
    }

    const newGateErrors: Record<string, string> = {};
    gates.forEach((gate) => {
      if (gate.isSystem) return;
      if (!gate.name.trim()) {
        newGateErrors[gate.gateId] = 'Gate name is required';
        isValid = false;
      }
      const hasEvaluate = gate.instructions.match(/Evaluate:\s*(.+)/i);
      const hasPass = gate.instructions.match(/PASS when:\s*(.+)/i);
      const hasFail = gate.instructions.match(/FAIL when:\s*(.+)/i);
      if (!hasEvaluate?.[1]?.trim() || !hasPass?.[1]?.trim() || !hasFail?.[1]?.trim()) {
        newGateErrors[gate.gateId] = newGateErrors[gate.gateId]
          || 'All instruction fields (Evaluate, PASS when, FAIL when) are required';
        isValid = false;
      }
    });
    setGateErrors(newGateErrors);

    return isValid;
  }, [name, gates]);

  // ============================================================================
  // SAVE
  // ============================================================================

  const handleSave = useCallback(() => {
    if (!validate()) return;

    // Phase 29: Sync template-level rolling window to all rules before save
    const rulesWithWindow = verdictRulesV2.map((r) => ({
      ...r,
      rollingWindowDays: rollingWindowDays ?? 7,
    }));

    if (isEditMode) {
      const data: UpdateTemplateRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        gates,
        diagnosisDimensions,
        kpiConfig,
        verdictRulesV2: rulesWithWindow,
        percentileSettings,
      };
      onSave(data);
    } else {
      const data: CreateTemplateRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        gates: gates.map(({ gateId: _id, ...rest }) => rest),
        // Phase 25: include all sections from starter
        diagnosisDimensions: diagnosisDimensions.length > 0 ? diagnosisDimensions : undefined,
        kpiConfig,
        verdictRulesV2: rulesWithWindow.length > 0 ? rulesWithWindow : undefined,
        starterKey: selectedStarterKey ?? undefined,
        percentileSettings,
      };
      onSave(data);
    }
  }, [isEditMode, name, description, gates, diagnosisDimensions, kpiConfig, verdictRulesV2, rollingWindowDays, percentileSettings, validate, onSave, selectedStarterKey]);

  // ============================================================================
  // RENDER — PRE-FORK (STARTER SELECTION)
  // ============================================================================

  if (!hasForked && !isEditMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-primary">
              Choose an Evaluation Philosophy
            </h2>
            <p className="text-sm text-secondary mt-1">
              Select a starter template to begin. You can customize everything after.
            </p>
          </div>
          <Button variant="ghost" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>

        {/* Starter Cards */}
        <StarterSelector
          onSelect={handleForkStarter}
          selectedKey={selectedStarterKey}
        />

        {/* Hint */}
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 text-xs text-muted">
            <ChevronRight className="h-3 w-3" />
            <span>Select a starter to configure gates, diagnosis, KPIs, and verdict rules</span>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER — POST-FORK (TABBED EDITOR)
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-primary">
          {isEditMode ? 'Edit Template' : 'Create Template'}
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            leftIcon={isSaving ? undefined : <Save className="h-4 w-4" />}
          >
            {isSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      {/* Name field */}
      <Input
        label="Template Name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (nameError) setNameError('');
        }}
        placeholder="e.g., Performance Video Template"
        maxLength={100}
        required
        error={!!nameError}
        errorMessage={nameError}
      />

      {/* Description field */}
      <div>
        <label className="mb-2 block text-sm font-medium text-secondary">
          Description (optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of when to use this template..."
          rows={2}
          maxLength={500}
          className="flex w-full font-sans text-md text-primary placeholder:text-muted transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-md border border-border-subtle bg-surface px-4 py-3 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12 resize-none"
        />
      </div>

      {/* Tabbed sections */}
      <TabsPrimitive.Root defaultValue="gates" className="w-full">
        <TabsList>
          <TabsTrigger value="gates">
            Gates ({gates.length})
          </TabsTrigger>
          <TabsTrigger value="diagnosis">
            Diagnosis ({enabledDimCount})
          </TabsTrigger>
          <TabsTrigger value="kpis">
            Performance KPIs{kpiConfig ? ` (${kpiConfig.mode})` : ''}
          </TabsTrigger>
          <TabsTrigger value="verdict">
            Verdict Rules ({verdictRulesV2.length})
          </TabsTrigger>
        </TabsList>

        {/* Gates Tab */}
        <TabsContent value="gates">
          <GatesTab
            gates={gates}
            gateErrors={gateErrors}
            isSaving={isSaving}
            onAddGate={handleAddGate}
            onUpdateGate={handleUpdateGate}
            onRemoveGate={handleRemoveGate}
            onMoveGateUp={handleMoveGateUp}
            onMoveGateDown={handleMoveGateDown}
          />
        </TabsContent>

        {/* Diagnosis Tab */}
        <TabsContent value="diagnosis">
          <DiagnosisSection
            dimensions={diagnosisDimensions}
            onChange={setDiagnosisDimensions}
          />
        </TabsContent>

        {/* Performance KPIs Tab */}
        <TabsContent value="kpis">
          <KpiSection
            kpiConfig={kpiConfig}
            onChange={setKpiConfig}
            customKpiOptions={customKpiOptions}
          />
        </TabsContent>

        {/* Verdict Rules Tab */}
        <TabsContent value="verdict">
          <VerdictRuleBuilder
            rules={verdictRulesV2}
            onChange={setVerdictRulesV2}
            gates={gates}
            dimensions={diagnosisDimensions.filter((d) => d.enabled)}
            selectedKpiIds={
              kpiConfig
                ? [
                    ...(kpiConfig.primaryKpiId ? [kpiConfig.primaryKpiId] : []),
                    ...kpiConfig.secondaryKpiIds,
                  ]
                : undefined
            }
            customKpiOptions={customKpiOptions}
            percentileSettings={percentileSettings}
            onPercentileSettingsChange={setPercentileSettings}
            rollingWindowDays={rollingWindowDays}
            onRollingWindowDaysChange={setRollingWindowDays}
          />
        </TabsContent>
      </TabsPrimitive.Root>
    </div>
  );
}
